import {useMemo} from 'react';
import {ProfileProps} from '@/types/creatorProfile';
import QueryContext from '@/api/QueryContext';
import campaignFetchers from '@/api/fetchers/campaign';
import useInfiniteDataQuery from '@/hooks/use-infinite-data-query';

interface UseCampaignSuggestedCreatorsOldQueryParams {
  campaignId?: string;
  perPage?: number;
  refetchOnWindowFocus?: boolean;
  enabled?: boolean;
  compact?: boolean;
  include?: string[];
  sessionId?: string;
}

interface Page {
  data: ProfileProps[];
  jsonapi: {version: string};
  meta: {
    pagination: {currentPage: number; totalPages: number; totalEntries: number};
  };
}

function useCampaignSuggestedCreatorsOldQuery({
  campaignId,
  perPage = 5,
  refetchOnWindowFocus = true,
  enabled = true,
  compact = true,
  include = [],
  sessionId,
}: UseCampaignSuggestedCreatorsOldQueryParams) {
  const response = useInfiniteDataQuery({
    queryKey: ['campaignSuggestedCreators', campaignId, perPage, include],
    queryFn: ({pageParam = 1, ...rest}: QueryContext) =>
      campaignFetchers.suggestedCreatorsOld({pageParam, ...rest}),
    enabled: enabled && !!campaignId,
    meta: {id: campaignId, perPage, compact, include, sessionId},
    retry: (failureCount: number, error: any) =>
      String(error?.code) !== '500' && failureCount < 3,
    refetchOnWindowFocus,
    keepPreviousData: false,
    getNextPageParam: (lastPage: Page) => {
      if (!lastPage.meta) {
        return undefined;
      }
      const {meta} = lastPage;
      return meta.pagination.currentPage < meta.pagination.totalPages
        ? meta.pagination.currentPage + 1
        : undefined;
    },
  });

  const {data = {}} = response;
  const {pages = []}: {pages?: Page[]} = data || {};
  const meta = pages[pages.length - 1]?.meta;

  const suggestedCreators = useMemo(() => {
    if (!response.data) {
      return [];
    }
    return pages.flatMap((page: {data: ProfileProps[]}) => page?.data);
  }, [response.data]);

  return {
    ...response,
    suggestedCreators,
    meta,
  };
}

export default useCampaignSuggestedCreatorsOldQuery;
